import { React, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { find, first, isEmpty, isNull, map, pick } from 'lodash'
import PropTypes from 'prop-types'

import { PUBLIC_URL } from '../../common/constants'
import { TireMeshIcon } from '../../icons'

const MESH_COORDS = [
  {
    axis: 'front',
    wheels: [
      { left: 8, top: 26 },
      { left: 184, top: 26 },
    ],
  },
  {
    axis: 'rear',
    wheels: [
      { left: 8, top: 249 },
      { left: 184, top: 249 },
    ],
  },
]
const StyledTabButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  height: '100%',
  backgroundColor: theme.palette.common.black,
  color: theme.palette.background.default,
  '&.Mui-active': {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.common.black,
    borderTop: '4px solid',
  },
  '&:not(.Mui-active):hover': {
    backgroundColor: '#444',
    color: theme.palette.background.default,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.gray.dark,
    color: theme.palette.background.default,
    borderTop: '4px solid',
  },
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.root}`]: { border: 0 },
  [`&.${tableCellClasses.root}:first-of-type`]: {
    color: theme.palette.gray.dark,
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.background.default,
  },
}))

const TireMeshImg = styled('img')({
  width: '240px',
  height: '380px',
})

const TireMeshPointer = styled('span')({
  position: 'absolute',
  zIndex: '100',
})

const TireMeshContainer = styled(Box)({
  position: 'relative',
  margin: 0,
  padding: 0,
  display: 'flex',
})

const TireIcon = styled(TireMeshIcon)(({ color }) => ({
  backgroundColor: color,
  borderRadius: '0.8rem',
}))

function TyresConditionList() {
  const list = [
    { title: 'Nowe' },
    { title: '25% zużycia' },
    { title: '50% zużycia' },
    { title: '75% zużycia' },
    { title: 'Do wymiany' },
  ]
  return (
    <List
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        pl: 2,
        height: 360,
        '&:before': {
          position: 'absolute',
          top: 8,
          left: 0,
          bottom: 8,
          display: 'block',
          content: '" "',
          height: 'calc(100% - 16px)',
          width: 12,
          background:
            'linear-gradient(180deg, #19BF0A 0%, #F1F512 34.76%, #F85C04 66.05%, #DA0000 100%)',
        },
      }}
    >
      {map(list, ({ title, subtitle }, key) => (
        <ListItem
          key={key}
          disablePadding
          sx={{
            padding: '0 !important',
            '&:before': { display: 'none !important' },
          }}
        >
          <ListItemText
            primary={title}
            primaryTypographyProps={{ variant: 'caption' }}
            secondary={subtitle}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </ListItem>
      ))}
    </List>
  )
}

function DigitalCertificateTyres({ certificateData }) {
  const { tyreConditions } = certificateData
  const [selected, setSelected] = useState(null)
  const [selectedTyreConditions, setSelectedTyreConditions] = useState({})
  const { t } = useTranslation()
  const { front, rear } = find(tyreConditions, { type: 'summer' }) || {}
  const isAllSeasons = (front?.isAllSeasons && rear?.isAllSeasons) || false

  useEffect(() => {
    if (isNull(selected)) {
      setSelected(first(tyreConditions).type)
    } else {
      setSelectedTyreConditions(find(tyreConditions, { type: selected }))
    }
  }, [selected, tyreConditions])

  const handleTabClick = (e) => {
    setSelected(e.target.id)
  }

  return (
    <>
      <Grid container spacing={10} sx={{ mt: 0 }}>
        <Grid container item xs={12} columnSpacing={1}>
          <Grid item xs={6}>
            <StyledTabButton
              id="summer"
              className={selected === 'summer' ? 'Mui-active' : ''}
              disabled={!find(tyreConditions, { type: 'summer' })}
              fullWidth
              onClick={handleTabClick}
            >
              {t(`tyres.types.${isAllSeasons ? 'allSeasons' : 'summer'}`)}
            </StyledTabButton>
          </Grid>
          <Grid item xs={6}>
            <StyledTabButton
              id="winter"
              className={selected === 'winter' ? 'Mui-active' : ''}
              disabled={!find(tyreConditions, { type: 'winter' })}
              fullWidth
              onClick={handleTabClick}
            >
              {t(`tyres.types.winter`)}
            </StyledTabButton>
          </Grid>
        </Grid>
      </Grid>
      <Paper elevation={0} sx={{ display: 'flex', mt: 1 }}>
        <Grid container spacing={3}>
          <Grid container item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                display: 'flex',
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  minWidth: 100,
                  display: 'flex',
                  flexDirection: 'column',
                  flexShrink: 0,
                }}
              >
                <Typography variant="h4">{t('controlsEditTires')}</Typography>
                <TyresConditionList />
              </Box>
              <Box sx={{ display: 'flex', flexShrink: 1 }}>
                {!isEmpty(selectedTyreConditions) && (
                  <TireMeshContainer
                    sx={{
                      transform: {
                        xs: 'scale(0.7)',
                        md: 'scale(0.9)',
                      },
                    }}
                  >
                    {map(MESH_COORDS, (axis) =>
                      map(axis.wheels, (wheel, key) => (
                        <TireMeshPointer key={key} sx={wheel}>
                          {selectedTyreConditions[axis.axis].conditionId ===
                            1 && <TireIcon color="#30c50b" />}
                          {selectedTyreConditions[axis.axis].conditionId ===
                            5 && <TireIcon color="#c0e910" />}
                          {selectedTyreConditions[axis.axis].conditionId ===
                            2 && <TireIcon color="#f4a90b" />}
                          {selectedTyreConditions[axis.axis].conditionId ===
                            3 && <TireIcon color="#f14703" />}
                          {selectedTyreConditions[axis.axis].conditionId ===
                            4 && <TireIcon color="#db0400" />}
                        </TireMeshPointer>
                      )),
                    )}
                    <TireMeshImg
                      src={`${PUBLIC_URL}/assets/images/tiremesh2.png`}
                    />
                  </TireMeshContainer>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 3 }}>
              {map(
                pick(selectedTyreConditions, ['front', 'rear']),
                (axis, key) => (
                  <Table key={key} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={({ palette }) => ({
                            backgroundColor: palette.gray.mediumDark,
                            border: 0,
                          })}
                        >
                          {t(`tyres.axis.${key}`)}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <StyledTableRow>
                        <StyledTableCell>
                          {t('tyres.props.make')}
                        </StyledTableCell>
                        <StyledTableCell>{axis.make}</StyledTableCell>
                      </StyledTableRow>
                      <StyledTableRow>
                        <StyledTableCell>
                          {t('tyres.props.model')}
                        </StyledTableCell>
                        <StyledTableCell>{axis.model}</StyledTableCell>
                      </StyledTableRow>
                      <StyledTableRow>
                        <StyledTableCell>
                          {t('tyres.props.size')}
                        </StyledTableCell>
                        <StyledTableCell>{axis.size}</StyledTableCell>
                      </StyledTableRow>
                      <StyledTableRow>
                        <StyledTableCell>
                          {t('tyres.props.age')}
                        </StyledTableCell>
                        <StyledTableCell>{axis.age}</StyledTableCell>
                      </StyledTableRow>
                    </TableBody>
                  </Table>
                ),
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  )
}

DigitalCertificateTyres.propTypes = {
  certificateData: PropTypes.object,
}
DigitalCertificateTyres.defaultProps = {
  certificateData: null,
}

export default DigitalCertificateTyres
